import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    Modal,
    ModalBody as Body,
    ModalFooter as Footer,
    ModalHeader as Header,
    ModalTitle as Title
} from "react-bootstrap";

import GroupedAction from "Entity/Collection/ListingSetting/GroupedAction";
import LoadingButton from "../../../Button/LoadingButton";
import { MailingActionModal } from "../../Listing";
import Api from "Vendor/Api";
import Collection from "Config/Collection";
import Property from "../../../../Entity/Property";
import RemoveType from "Config/File/RemoveType";

export interface _FileRemoveModalProps {
    collection?: Collection;
    showActionModals: { [key: string]: boolean | Property };
    handleShowActionModal: (groupedAction?: GroupedAction, actionFromMain?: boolean) => void;
    fromMain: boolean;
    mailingListActionType: MailingActionModal;
    setMailingListActionType: (mailingListActionType: MailingActionModal) => void;
    handleShowMainActionModal: () => void;
    selected: number[];
    clearSelection: () => void;
    actionModalFormIsLoading: boolean;
    handleActionModalOnResponse: () => void;
    handleActionModalOnSuccess: () => void;
    handleActionModalOnSubmit: () => void;
}

const _FileRemoveModal: FC<_FileRemoveModalProps> = ({
    collection,
    showActionModals,
    handleShowActionModal,
    fromMain,
    mailingListActionType,
    setMailingListActionType,
    handleShowMainActionModal,
    selected,
    clearSelection,
    actionModalFormIsLoading,
    handleActionModalOnResponse,
    handleActionModalOnSuccess,
    handleActionModalOnSubmit,
}) => {
    const { t } = useTranslation();

    const [removeType, setRemoveType] = useState<string | undefined>(undefined)

    const handleRemoveTypeChange = (value: string) => {
        setRemoveType(value);
    };

    const onSubmit = () => {
        if (!removeType) return;

        handleActionModalOnSubmit();

        Api.delete('/grouped-action/remove', {
            data: {
                listing: collection,
                selected: selected,
                removeType: removeType,
            }
        })
            .then(({ data }) => {
                clearSelection();
                handleActionModalOnSuccess();
            })
            .catch(() => { })
            .then(() => {
                handleActionModalOnResponse();
            });
    };

    return (
        <Modal show={showActionModals.remove !== false} onHide={() => handleShowActionModal()}>
            <Header closeButton={!fromMain}>
                <div className="modal-block-header">
                    {fromMain && <i className="fa-solid fa-angle-left" onClick={() => {
                        if (mailingListActionType === undefined) {
                            handleShowMainActionModal();
                        } else {
                            setMailingListActionType(undefined);
                        }
                    }}></i>}
                    <Title>
                        {t(`app.groupedActionTitle.remove.${collection}`, { count: selected.length })}
                    </Title>
                </div>
            </Header>

            <Body>
              <div className="d-flex align-items-center mb-2" style={{ color: 'black' }}>
                    <i className="fa-solid fa-trash fa-1x me-2"></i>
                    <span style={{ fontSize: '1rem', fontWeight: 'bold' }}>
                        {t('app.delete')}
                    </span>
                </div>
                <div className="mb-3" style={{ marginLeft: '0.5rem' }}>
                    <div className="form-check">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="removeType"
                            id={RemoveType.AllVersions}
                            value={RemoveType.AllVersions}
                            checked={removeType === RemoveType.AllVersions}
                            onChange={() => handleRemoveTypeChange(RemoveType.AllVersions)}
                        />
                        <label htmlFor={RemoveType.AllVersions} className="form-check-label">
                            {t('app.groupedActionContent.remove.all_versions')}
                        </label>
                    </div>

                    <div className="form-check mt-2">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="removeType"
                            id={RemoveType.LastVersion}
                            value={RemoveType.LastVersion}
                            checked={removeType === RemoveType.LastVersion}
                            onChange={() => handleRemoveTypeChange(RemoveType.LastVersion)}
                        />
                        <label htmlFor={RemoveType.LastVersion} className="form-check-label">
                            {t('app.groupedActionContent.remove.last_version')}
                        </label>
                    </div>
                </div>
                <span style={{ fontWeight: 'bold' }}>
                    {t(`app.groupedActionContent.remove.${collection}`, {count: selected.length})}
                </span>
            </Body>

            <Footer>
                {!fromMain && (
                    <Button variant="secondary" onClick={() => handleShowActionModal()}>
                        {t('app.cancel')}
                    </Button>
                )}

                <LoadingButton
                    variant="danger"
                    isLoading={actionModalFormIsLoading}
                    onClick={onSubmit}
                    disabled={!removeType}
                >
                    {t('app.archive')}
                </LoadingButton>
            </Footer>
        </Modal>
    );
};

export default _FileRemoveModal;
